"use client";

import { MessageCircle, Mail, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

export type NotificationType = "iMessage" | "gmail";

export interface NotificationAction {
  label: string;
  onClick: () => void;
}

// Email data for notifications that can open an email preview
export interface NotificationEmailData {
  from: string;
  to: string;
  subject: string;
  emailKey:
    | "loan-approved"
    | "payment-initiated"
    | "bank-connected"
    | "loan-initiated"; // Extend this union as more email templates are added
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  subtitle: string;
  message: string;
  timestamp: Date;
  icon?: string;
  autoDismiss?: boolean; // Defaults to false - notifications stay open until closed
  action?: NotificationAction; // Optional action button (e.g., "Open" for emails)
  emailData?: NotificationEmailData; // For email notifications that can be opened
}

interface NotificationProps {
  notification: Notification;
  onDismiss: () => void;
}

const NotificationBanner: React.FC<NotificationProps> = ({
  notification,
  onDismiss,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setIsVisible(true), 100);

    // Only auto-dismiss if explicitly enabled (defaults to staying open)
    let dismissTimer: NodeJS.Timeout | undefined;
    if (notification.autoDismiss) {
      dismissTimer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          onDismiss();
        }, 300);
      }, 6000);
    }

    return () => {
      clearTimeout(showTimer);
      if (dismissTimer) clearTimeout(dismissTimer);
    };
  }, [onDismiss, notification.autoDismiss]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss();
    }, 300);
  };

  const getNotificationStyles = () => {
    if (notification.type === "iMessage") {
      return {
        wrapper: "bg-gradient-to-r from-blue-500 to-blue-600 text-white",
        icon: "bg-white/20",
      };
    }
    return {
      wrapper: "bg-white border border-gray-200 text-gray-900",
      icon: "bg-red-500 text-white",
    };
  };

  const styles = getNotificationStyles();
  const Icon = notification.type === "iMessage" ? MessageCircle : Mail;

  return (
    <div
      className={`w-[calc(100vw-32px)] max-w-96 rounded-2xl shadow-2xl transition-all duration-300 ease-out ${styles.wrapper} ${isVisible && !isExiting ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"} `}
    >
      <div className="relative p-4">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 rounded-full p-1 transition-colors hover:bg-white/10"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3">
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${styles.icon}`}
          >
            <Icon className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1 pr-6">
            <div className="mb-1 flex items-center gap-2">
              <span className="text-sm font-semibold">
                {notification.title}
              </span>
              <span className="text-xs opacity-75">
                {new Date(notification.timestamp).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>

            <div className="mb-1 text-sm font-medium opacity-90">
              {notification.subtitle}
            </div>

            <div
              className={`text-sm ${notification.type === "iMessage" ? "opacity-90" : "text-gray-600"}`}
            >
              {notification.message}
            </div>

            {notification.action && (
              <button
                onClick={notification.action.onClick}
                className={`mt-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  notification.type === "iMessage"
                    ? "bg-white/20 text-white hover:bg-white/30"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
              >
                {notification.action.label}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface NotificationSystemProps {
  children: React.ReactNode;
}

interface NotificationContextType {
  showNotification: (
    notification: Omit<Notification, "id" | "timestamp">
  ) => void;
  notifications: Notification[];
}

const NotificationContext = React.createContext<NotificationContextType | null>(
  null
);

export const useNotifications = () => {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider: React.FC<NotificationSystemProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const showNotification = (
    notification: Omit<Notification, "id" | "timestamp">
  ) => {
    const newNotification: Notification = {
      ...notification,
      id: Math.random().toString(36).substring(2, 11),
      timestamp: new Date(),
    };

    setNotifications((prev) => [...prev, newNotification]);
  };

  const dismissNotification = React.useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification, notifications }}>
      {children}
      {mounted &&
        notifications.length > 0 &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: 16,
              right: 16,
              zIndex: 2147483647,
              pointerEvents: "none",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {notifications.map((notification) => (
              <div
                key={notification.id}
                style={{
                  pointerEvents: "auto",
                }}
              >
                <NotificationBanner
                  notification={notification}
                  onDismiss={() => dismissNotification(notification.id)}
                />
              </div>
            ))}
          </div>,
          document.body
        )}
    </NotificationContext.Provider>
  );
};

export const generateVerificationCodeNotification = (
  phoneNumber: string,
  code: string
): Omit<Notification, "id" | "timestamp"> => ({
  type: "iMessage",
  title: "Messages",
  subtitle: phoneNumber,
  message: `Your Prime verification code is: ${code}`,
});

export const generateApprovalNotification = (
  email: string
): Omit<Notification, "id" | "timestamp"> => {
  void email;
  return {
    type: "gmail",
    title: "Gmail",
    subtitle: `Prime Financial <no-reply@pop.loans>`,
    message: "Congratulations! Your loan application has been approved...",
  };
};

export const generateDocumentRequestNotification = (
  email: string
): Omit<Notification, "id" | "timestamp"> => {
  void email;
  return {
    type: "gmail",
    title: "Gmail",
    subtitle: `Prime Financial <documents@pop.loans>`,
    message: "We need additional documents to process your application...",
  };
};

export const generatePlaidNotification = (
  phoneNumber: string
): Omit<Notification, "id" | "timestamp"> => {
  void phoneNumber;
  return {
    type: "iMessage",
    title: "Messages",
    subtitle: "273-33",
    message: "Your Plaid verification code is: 123456. Do not share this code.",
  };
};
